import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Combobox, Search, Select } from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { useCampaignName, useNameSearch, useWebhookCampaigns } from '@/services/db/activity-log/queries';
import type { NameSearchKind } from '@/services/db/activity-log/entity';
import { LOG_TYPE } from '@/features/activity-feed';

/**
 * Contextual name filter, revealed once an Activity Type is picked:
 *  - Event → a server-searched Combobox (any event is findable, not just the cached sent_events)
 *  - Attribute Change → a server-searched Combobox (any attribute is findable, not just sent_attributes)
 *  - Geofence → a Select of the transition names (the wire `name` is `enter` / `exit`)
 *  - Profile Create / Delete → a free-text search
 *  - Webhook Arrived → a searchable webhook-campaign picker (filters by campaign id)
 *  - other types → nothing
 *
 * Deferred: object/relationship attribute-name options (per object-type) + the object-type dropdown.
 */

const FREE_TEXT_TYPES: string[] = [LOG_TYPE.ProfileCreate, LOG_TYPE.ProfileDelete];

type NameOption = { value: string; label: string };

// Geofence transition events are named by their transition; the wire values stay
// lowercase while the labels match the capitalized rendering in the table.
const GEOFENCE_TRANSITION_OPTIONS: NameOption[] = [
  { value: 'enter', label: 'Enter' },
  { value: 'exit', label: 'Exit' },
];

function nameOptions(type: string): NameOption[] {
  if (type === LOG_TYPE.Geofence) return GEOFENCE_TRANSITION_OPTIONS;
  return [];
}

function nameLabel(type: string): string {
  if (type === LOG_TYPE.Geofence) return 'Transition';
  return 'Name';
}

/**
 * Free-text name search (Profile Create/Delete). Local input state, committed to the URL `name`
 * on a 1s debounce/clear (avoids a refetch per keystroke). The parent keys `<NameFilter>` on the
 * activity type, so this remounts whenever the type changes — resetting the seeded input and
 * letting Pluma's debounce cleanup cancel any pending `onSearch`, so a stale name can't be reapplied.
 */
type FreeTextNameFilterProps = { label: string; name: string; onChange: (n: string) => void };

function FreeTextNameFilter({ label, name, onChange }: FreeTextNameFilterProps) {
  const [value, setValue] = useState(name);
  return (
    <Search
      ariaLabel={label}
      placeholder="any"
      value={value}
      onSearchInput={setValue}
      onSearch={(next) => onChange(next ?? '')}
      shouldSearchOnDebounce={1000}
      shouldSearchOnClear
      size="sm"
    />
  );
}

/**
 * Webhook campaign picker (Webhook Arrived). The logs API filters webhook activity by campaign id,
 * so this resolves a campaign name → its id. Options are searched server-side (the campaigns list
 * endpoint pages at 20), so the Combobox's own filtering is disabled and each keystroke drives a
 * debounced refetch. The input text is fully controlled: a deep link supplies only the id in `name`,
 * which is resolved to a campaign name to seed the field.
 */
type WebhookCampaignFilterProps = { environmentId: string; name: string; onChange: (name: string) => void };

function WebhookCampaignFilter({ environmentId, name, onChange }: WebhookCampaignFilterProps) {
  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const seededRef = useRef(false);

  const { campaigns, isLoading } = useWebhookCampaigns(environmentId, search);
  const selectedName = useCampaignName(environmentId, name);

  // Seed the input from the resolved campaign name once, for a filter populated by a deep link
  // (only the id is in the URL). After that the user's typing/selection owns the input.
  useEffect(() => {
    if (!seededRef.current && !isEmptyString(name) && selectedName != null) {
      setInputValue(selectedName);
      seededRef.current = true;
    }
  }, [name, selectedName]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  function handleValueChange(text: string) {
    setInputValue(text);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(text.trim()), 300);
  }

  function handleSelectionChange(value: string | null) {
    clearTimeout(debounceRef.current);
    setSearch('');
    if (value == null) {
      setInputValue('');
      onChange('');
      return;
    }
    setInputValue(campaigns.find((campaign) => campaign.id === value)?.name ?? '');
    onChange(value);
  }

  function handleClear() {
    clearTimeout(debounceRef.current);
    setSearch('');
    setInputValue('');
    onChange('');
  }

  return (
    <Combobox
      label="Campaign"
      placeholder="any"
      shouldFilter={false}
      isLoading={isLoading}
      isClearable
      onClear={handleClear}
      inputValue={inputValue}
      selectedValue={isEmptyString(name) ? null : name}
      options={campaigns.map((campaign) => ({ label: campaign.name, value: campaign.id }))}
      onValueChange={handleValueChange}
      onSelectionChange={handleSelectionChange}
      size="sm"
    />
  );
}

/**
 * Searchable name picker for the Event and Attribute Change types. Both endpoints filter names
 * server-side, so a name outside the cached list (`sentEvents` / `sentAttributes`) is still findable —
 * the regression this restores. The cached list seeds the options for the empty (unsearched) state;
 * each keystroke drives a debounced server search, so the Combobox's own filtering is disabled. Here
 * `value` is the name itself, so a deep-linked `name` just seeds the input directly (no id resolution).
 */
type SearchableNameFilterProps = {
  kind: NameSearchKind;
  label: string;
  environmentId: string;
  name: string;
  cachedNames: string[];
  onChange: (name: string) => void;
};

function SearchableNameFilter({ kind, label, environmentId, name, cachedNames, onChange }: SearchableNameFilterProps) {
  const [inputValue, setInputValue] = useState(name);
  const [search, setSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Pluma's Combobox only ever commits a keyboard-highlighted option (arrow keys), never
  // plain typed text — Enter on an un-highlighted input just closes the dropdown. Track the
  // highlighted option ourselves so Enter can fall back to committing the raw typed name,
  // which lets support/customers confirm a name genuinely has no matches (can't select what
  // isn't there).
  const activeValueRef = useRef<string | null>(null);

  const { names: searchedNames, isLoading } = useNameSearch(kind, environmentId, search);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  function handleValueChange(text: string) {
    setInputValue(text);
    clearTimeout(debounceRef.current);
    // Emptying the field clears the active filter — otherwise the URL `name` lingers behind a blank input.
    if (isEmptyString(text)) {
      setSearch('');
      onChange('');
      return;
    }
    debounceRef.current = setTimeout(() => setSearch(text.trim()), 300);
  }

  function handleSelectionChange(value: string | null) {
    // Reset search so reopening the combobox falls back to the cached list, not the last server results.
    clearTimeout(debounceRef.current);
    setSearch('');
    setInputValue(value ?? '');
    onChange(value ?? '');
  }

  function handleClear() {
    clearTimeout(debounceRef.current);
    setSearch('');
    setInputValue('');
    onChange('');
  }

  const names = isEmptyString(search) ? cachedNames : searchedNames;

  // Mirrors the legacy Ember picker (attribute-autocomplete's plumaOptions getter): list the
  // raw typed text as the first option whenever it doesn't already match one, so a name with
  // no matches reads as something the user can click/arrow-select, not a dead end.
  const options = names.map((n) => ({ label: n, value: n }));
  if (!isEmptyString(inputValue) && !names.includes(inputValue)) {
    options.unshift({ label: inputValue, value: inputValue });
  }

  // A wrapper is required: passing onKeyDown straight to <Combobox> is silently dropped,
  // since Pluma's own internal keydown handler always wins over one passed in as a prop.
  // Routes through handleSelectionChange so this path resets the same state a real
  // selection does (pending debounce, search, and the trimmed input value).
  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && activeValueRef.current == null) {
      handleSelectionChange(inputValue.trim());
    }
  }

  return (
    // role="presentation": purely an event-capturing wrapper around the Combobox's own
    // input, not an independent interactive control (a11y linters otherwise flag a static
    // element with a key handler). `display: contents` keeps it box-less so the Combobox
    // stays the actual flex item in the parent toolbar's InlineStack, unaffected by the
    // extra DOM layer (keydown still bubbles through regardless of the box tree).
    <div role="presentation" onKeyDown={handleKeyDown} style={{ display: 'contents' }}>
      <Combobox
        label={label}
        placeholder="any"
        shouldFilter={false}
        isLoading={isLoading}
        isClearable
        onClear={handleClear}
        inputValue={inputValue}
        selectedValue={isEmptyString(name) ? null : name}
        options={options}
        onValueChange={handleValueChange}
        onSelectionChange={handleSelectionChange}
        onActiveChange={(value) => {
          activeValueRef.current = value;
        }}
        size="sm"
      />
    </div>
  );
}

interface NameFilterProps {
  type: string;
  name: string;
  environmentId: string;
  sentEvents: string[];
  sentAttributes: string[];
  onChange: (name: string) => void;
}

export function NameFilter({ type, name, environmentId, sentEvents, sentAttributes, onChange }: NameFilterProps) {
  if (isEmptyString(type)) return null;

  if (type === LOG_TYPE.WebhookEvent) {
    return <WebhookCampaignFilter environmentId={environmentId} name={name} onChange={onChange} />;
  }

  if (FREE_TEXT_TYPES.includes(type)) {
    return <FreeTextNameFilter label={nameLabel(type)} name={name} onChange={onChange} />;
  }

  if (type === LOG_TYPE.Event) {
    return (
      <SearchableNameFilter
        kind="event"
        label="Event name"
        environmentId={environmentId}
        name={name}
        cachedNames={sentEvents}
        onChange={onChange}
      />
    );
  }

  if (type === LOG_TYPE.AttributeChange) {
    return (
      <SearchableNameFilter
        kind="attribute"
        label="Attribute name"
        environmentId={environmentId}
        name={name}
        cachedNames={sentAttributes}
        onChange={onChange}
      />
    );
  }

  const options = nameOptions(type);
  if (options.length === 0) return null;

  return (
    <Select
      label={nameLabel(type)}
      placeholder="any"
      value={isEmptyString(name) ? undefined : name}
      onChange={(value) => onChange(value ?? '')}
      options={options}
      isSearchable
      isClearable
      size="sm"
    />
  );
}
